/**
 * FERP Portal - Role Display Name Resolution Hook
 *
 * Resolves role IDs (e.g., "core-018") to display names (e.g., "Medication Aide").
 * DRR-324: Legislation-linked role names for user-facing UI.
 *
 * Uses static map from role configs. Role configs define:
 *   - role_id: core-018 (portable ID)
 *   - display_name: "Medication Aide" (user-facing)
 *   - statute_ref: "144G.71" (legislation link)
 */

/**
 * Static role display name map.
 * Source: data/configs/roles/*.yaml display_name fields
 * DRR-194: Role IDs are legislation-derived, display names are user-friendly.
 */
const ROLE_DISPLAY_NAMES: Record<string, string> = {
  // Core roles
  'core-001': 'Base User',
  'core-011': 'Assisted Living Director',
  'core-012': 'Director of Nursing',
  'core-013': 'Charge Nurse',
  'core-014': 'Registered Nurse',
  'core-015': 'Licensed Practical Nurse',
  'core-016': 'Unlicensed Personnel — Basic',
  'core-017': 'Unlicensed Personnel — Delegated',
  'core-018': 'Medication Aide',
  // Owner/Admin
  'ceo-owner': 'CEO / Owner',
  // Legacy aliases (for backwards compat)
  'ALD': 'Assisted Living Director',
  'DON': 'Director of Nursing',
};

/**
 * Hook to resolve role IDs to display names.
 *
 * @returns Object with:
 *   - getRoleDisplayName(roleId): Returns display name or roleId if not found
 *   - loading: Always false (static map)
 */
export function useRoleDisplayName() {
  const getRoleDisplayName = (roleId: string | null | undefined): string => {
    if (!roleId) return '';
    return ROLE_DISPLAY_NAMES[roleId] ?? roleId;
  };

  return {
    getRoleDisplayName,
    loading: false,
  };
}
